import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  BulkInviteRequest,
  ReferralAnalytics,
  ReferralCodeResponse,
  ReferralConfigResponse,
  ReferralEarningsListResponse,
  ReferralEarningsSummaryResponse,
  ReferralHistoryResponse,
  ReferralLeaderboardEntry,
  ReferralLevelsResponse,
  ReferralSettingsUpdateRequest,
  ReferralSettings,
  ReferralStatsResponse,
  ReferralUser,
  ReferralWithdrawRequest,
} from '../../types/api/referrals';
import type { UCBalance } from '../../types/api/uc';
import { referralsApi } from '../../services/api/referrals.api';

interface ReferralState {
  profile: ReferralUser | null;
  code: ReferralCodeResponse | null;
  earnings: ReferralEarningsListResponse | null;
  earningsSummary: ReferralEarningsSummaryResponse | null;
  referrals: {
    direct: ReferralUser[];
    indirect: ReferralUser[];
  };
  stats: ReferralStatsResponse | null;
  config: ReferralConfigResponse | null;
  levels: ReferralLevelsResponse | null;
  analytics: ReferralAnalytics | null;
  leaderboard: ReferralLeaderboardEntry[];
  history: ReferralHistoryResponse | null;
  ucBalance: UCBalance | null;
  settings: ReferralSettings | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReferralState = {
  profile: null,
  code: null,
  earnings: null,
  earningsSummary: null,
  referrals: { direct: [], indirect: [] },
  stats: null,
  config: null,
  levels: null,
  analytics: null,
  leaderboard: [],
  history: null,
  ucBalance: null,
  settings: null,
  loading: false,
  error: null,
};

export const fetchReferralProfile = createAsyncThunk(
  'referral/fetchProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.getProfile(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки профиля');
    }
  }
);

export const createReferralProfile = createAsyncThunk(
  'referral/createProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.createProfile(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка создания профиля');
    }
  }
);

export const fetchReferralCode = createAsyncThunk(
  'referral/fetchCode',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.getCode(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки кода');
    }
  }
);

export const fetchReferralStats = createAsyncThunk(
  'referral/fetchStats',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.getStats(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки статистики');
    }
  }
);

export const fetchReferralConfig = createAsyncThunk(
  'referral/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      return await referralsApi.getConfig();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки конфигурации');
    }
  }
);

export const fetchReferralLevels = createAsyncThunk(
  'referral/fetchLevels',
  async (userId: string | undefined, { rejectWithValue }) => {
    try {
      return await referralsApi.getLevels(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки уровней');
    }
  }
);

export const fetchReferralEarnings = createAsyncThunk(
  'referral/fetchEarnings',
  async (params: { userId: string; status?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await referralsApi.getEarnings(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки заработков');
    }
  }
);

export const fetchReferralEarningsSummary = createAsyncThunk(
  'referral/fetchEarningsSummary',
  async (params: { userId: string; period?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await referralsApi.getEarningsSummary(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки сводки');
    }
  }
);

export const fetchReferralReferrals = createAsyncThunk(
  'referral/fetchReferrals',
  async (params: { userId: string; level?: number }, { rejectWithValue }) => {
    try {
      return await referralsApi.getReferrals(params.userId, params.level);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки рефералов');
    }
  }
);

export const fetchReferralHistory = createAsyncThunk(
  'referral/fetchHistory',
  async (params: { userId: string; page?: number; limit?: number; type?: string }, { rejectWithValue }) => {
    try {
      return await referralsApi.getHistory(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки истории');
    }
  }
);

export const fetchReferralAnalytics = createAsyncThunk(
  'referral/fetchAnalytics',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.getAnalytics(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки аналитики');
    }
  }
);

export const fetchReferralLeaderboard = createAsyncThunk(
  'referral/fetchLeaderboard',
  async (_, { rejectWithValue }) => {
    try {
      return await referralsApi.getLeaderboard();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки лидеров');
    }
  }
);

export const sendBulkInvites = createAsyncThunk(
  'referral/sendBulkInvites',
  async (payload: BulkInviteRequest, { rejectWithValue }) => {
    try {
      return await referralsApi.sendBulkInvites(payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка отправки приглашений');
    }
  }
);

export const requestReferralWithdraw = createAsyncThunk(
  'referral/requestWithdraw',
  async (payload: ReferralWithdrawRequest, { rejectWithValue }) => {
    try {
      return await referralsApi.withdraw(payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка вывода средств');
    }
  }
);

export const fetchReferralSettings = createAsyncThunk(
  'referral/fetchSettings',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await referralsApi.getSettings(userId);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки настроек');
    }
  }
);

export const updateReferralSettings = createAsyncThunk(
  'referral/updateSettings',
  async (params: { userId: string; data: ReferralSettingsUpdateRequest }, { rejectWithValue }) => {
    try {
      return await referralsApi.updateSettings(params.userId, params.data);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка сохранения настроек');
    }
  }
);

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearReferralError: (state) => {
      state.error = null;
    },
    setUCBalance: (state, action: { payload: UCBalance }) => {
      state.ucBalance = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferralProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchReferralProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createReferralProfile.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(fetchReferralCode.fulfilled, (state, action) => {
        state.code = action.payload;
      })
      .addCase(fetchReferralStats.fulfilled, (state, action) => {
        state.stats = action.payload;
        state.ucBalance =
          action.payload.ucBalance || {
            available: action.payload.currentBalance,
            breakdown: [],
          };
      })
      .addCase(fetchReferralConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(fetchReferralLevels.fulfilled, (state, action) => {
        state.levels = action.payload;
      })
      .addCase(fetchReferralEarnings.fulfilled, (state, action) => {
        state.earnings = action.payload;
      })
      .addCase(fetchReferralEarningsSummary.fulfilled, (state, action) => {
        state.earningsSummary = action.payload;
      })
      .addCase(fetchReferralReferrals.fulfilled, (state, action) => {
        const level = action.meta.arg.level;
        if (level === 2) {
          state.referrals.indirect = action.payload;
        } else {
          state.referrals.direct = action.payload;
        }
      })
      .addCase(fetchReferralHistory.fulfilled, (state, action) => {
        state.history = action.payload;
      })
      .addCase(fetchReferralAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      })
      .addCase(fetchReferralLeaderboard.fulfilled, (state, action) => {
        state.leaderboard = action.payload;
      })
      .addCase(fetchReferralSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(updateReferralSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith('referral/') && action.type.endsWith('/pending'),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('referral/') && action.type.endsWith('/rejected'),
        (state, action: any) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('referral/') && action.type.endsWith('/fulfilled'),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearReferralError, setUCBalance } = referralSlice.actions;
export default referralSlice.reducer;
